// Chemin: C:\gestion-copro\src\pages\auth\LoginPage.tsx
// Page de connexion pour COPRO PROTECT
// Session 45 : Intégration logo Copro Protect + lien retour accueil
// Session 54 : Redirection intelligente selon profil (Admin SaaS / Secrétaire / etc.)
// Session 55 : Ajout fonctionnalité "Mot de passe oublié"
// Session 56 : Redirection copropriétaires vers /espace-copro
// Session 101 : Ajout redirection vigie vers /vigie
// Session 150 : Redirection en_attente_paiement vers confirmation + messages statut

// Chemin: C:\gestion-copro\src\pages\auth\LoginPage.tsx
// Page de connexion pour COPRO PROTECT
// Session 45 : Intégration logo Copro Protect + lien retour accueil
// Session 54 : Redirection intelligente selon profil (Admin SaaS / Secrétaire / etc.)
// Session 55 : Ajout fonctionnalité "Mot de passe oublié"
// Session 56 : Redirection copropriétaires vers /espace-copro
// Session 101 : Ajout redirection vigie vers /vigie
// Session 150 : Redirection en_attente_paiement vers confirmation + messages statut
// Session 163 : Reset mot de passe via Cloud Function Mailjet (au lieu de Firebase natif)

import React, { useState, useEffect, FormEvent } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../../context/AuthContext';
import { ArrowLeft, Shield, Mail, CheckCircle } from 'lucide-react';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pendingRedirect, setPendingRedirect] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  
  // États pour la réinitialisation du mot de passe
  const [showResetForm, setShowResetForm] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSending, setResetSending] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  
  const { signIn, error, clearError, userProfile, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Récupérer le contexte copropriété depuis sessionStorage (si vient d'un portail)
  const pendingCoproprieteSlug = sessionStorage.getItem('pendingCoproprieteSlug');

  // Récupérer la page d'origine si redirection forcée
  const from = (location.state as any)?.from?.pathname;

  // ============================================
  // REDIRECTION APRÈS AUTHENTIFICATION RÉUSSIE
  // ============================================
  
  useEffect(() => {
    if (isAuthenticated && userProfile && pendingRedirect) {
      // Session 150 : Gestion des statuts non actifs
      if (userProfile.status === 'en_attente_paiement') {
        // Rediriger vers la page de confirmation avec le mode "canceled"
        navigate('/confirmation-vigie?canceled=true', { replace: true });
        return;
      }

      if (userProfile.status === 'rejected') {
        setStatusMessage('Votre compte a été désactivé. Veuillez contacter le support pour plus d\'informations.');
        setPendingRedirect(false);
        return;
      }

      if (userProfile.status !== 'active') {
        setStatusMessage('Votre compte n\'est pas encore actif. Veuillez patienter ou contacter le support.');
        setPendingRedirect(false);
        return;
      }

      // Priorité 1 : Admin SaaS
      if (userProfile.isGestionnaireSaas) {
        navigate('/admin-saas', { replace: true });
        return;
      }
      
      // Priorité 2 : Page d'origine demandée
      if (from) {
        navigate(from, { replace: true });
        return;
      }
      
      // Priorité 3 : Copropriétaire → Espace copropriétaire
      if (userProfile.role === 'coproprietaire') {
        navigate('/espace-copro', { replace: true });
        return;
      }

      // Priorité 4 : Vigie → Espace vigie
      if (userProfile.role === 'vigie') {
        navigate('/vigie', { replace: true });
        return;
      }
      
      // Priorité 5 : CS ou Gestionnaire → Interface admin
      navigate('/admin', { replace: true });
    }
  }, [isAuthenticated, userProfile, pendingRedirect, from, navigate]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    clearError();
    setStatusMessage(null);
    setIsSubmitting(true);

    try {
      await signIn(email, password);
      setPendingRedirect(true);
    } catch (err) {
      // L'erreur est déjà gérée dans le context
    } finally {
      setIsSubmitting(false);
    }
  };

  // Gestion de la réinitialisation du mot de passe — Session 163 : via Cloud Function Mailjet
  const handleResetPassword = async (e: FormEvent) => {
    e.preventDefault();
    setResetError(null);
    setResetSending(true);

    try {
      const functions = getFunctions(undefined, 'europe-west1');
      const resetPassword = httpsCallable<{ email: string; adminMode: boolean }, { success: boolean }>(
        functions, 'resetUserPassword'
      );

      await resetPassword({ email: resetEmail.trim().toLowerCase(), adminMode: false });
      setResetSuccess(true);
    } catch (err: any) {
      console.error('Erreur réinitialisation:', err);
      
      const code = err?.code || '';
      const message = err?.message || '';

      if (code === 'functions/not-found' || message.includes('Aucun compte')) {
        setResetError('Aucun compte associé à cette adresse email');
      } else if (code === 'functions/invalid-argument') {
        setResetError('Adresse email invalide');
      } else {
        setResetError('Une erreur est survenue. Veuillez réessayer.');
      }
    } finally {
      setResetSending(false);
    }
  };

  // Retour au formulaire de connexion
  const handleBackToLogin = () => {
    setShowResetForm(false);
    setResetSuccess(false);
    setResetError(null);
    setResetEmail('');
  };

  // Ouvrir le formulaire de réinitialisation avec l'email pré-rempli
  const handleShowResetForm = () => {
    setResetEmail(email);
    setShowResetForm(true);
    clearError();
  };

  // Déterminer le lien de retour (portail copro ou accueil)
  const backLink = pendingCoproprieteSlug ? `/${pendingCoproprieteSlug}` : '/';
  const backLabel = pendingCoproprieteSlug ? 'Retour au portail' : 'Retour à l\'accueil';

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 to-blue-700 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8">
        {/* Logo et titre */}
        <div className="text-center mb-8">
          <img 
            src="/logo-copro-protect.png" 
            alt="Copro Protect" 
            className="h-20 mx-auto mb-4"
          />
          <p className="text-gray-500">
            {showResetForm ? 'Réinitialisation du mot de passe' : 'Connexion à votre espace'}
          </p>
        </div>

        {/* ==================== FORMULAIRE RÉINITIALISATION ==================== */}
        {showResetForm ? (
          <>
            {resetSuccess ? (
              // Message de succès
              <div className="text-center py-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="text-green-600" size={32} />
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  Email envoyé !
                </h3>
                <p className="text-gray-600 mb-4">
                  Un email de réinitialisation a été envoyé à <strong>{resetEmail}</strong>.
                </p>
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg mb-6">
                  <p className="text-amber-800 text-sm">
                    <strong>Important :</strong> Si vous ne trouvez pas l'email, pensez à vérifier votre dossier <strong>Spams</strong> ou <strong>Courrier indésirable</strong>.
                  </p>
                </div>
                <button
                  onClick={handleBackToLogin}
                  className="w-full py-3 px-4 rounded-lg font-semibold text-white bg-blue-900 hover:bg-blue-800 transition-colors"
                >
                  Retour à la connexion
                </button>
              </div>
            ) : (
              // Formulaire de réinitialisation
              <>
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-blue-700 text-sm flex items-start gap-2">
                    <Mail className="flex-shrink-0 mt-0.5" size={18} />
                    <span>
                      Saisissez votre adresse email. Vous recevrez un lien pour créer un nouveau mot de passe.
                    </span>
                  </p>
                </div>

                {resetError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-700 text-sm">{resetError}</p>
                  </div>
                )}

                <form onSubmit={handleResetPassword} className="space-y-4">
                  <div>
                    <label htmlFor="resetEmail" className="block text-sm font-medium text-gray-700 mb-2">
                      Adresse email
                    </label>
                    <input
                      type="email"
                      id="resetEmail"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="votre@email.com"
                      required
                      disabled={resetSending}
                      autoComplete="email"
                      autoFocus
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={resetSending}
                    className={`
                      w-full py-3 px-4 rounded-lg font-semibold text-white
                      transition-all duration-200
                      ${resetSending 
                        ? 'bg-gray-400 cursor-not-allowed' 
                        : 'bg-blue-900 hover:bg-blue-800 hover:shadow-lg'
                      }
                    `}
                  >
                    {resetSending ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle 
                            className="opacity-25" 
                            cx="12" cy="12" r="10" 
                            stroke="currentColor" 
                            strokeWidth="4"
                            fill="none"
                          />
                          <path 
                            className="opacity-75" 
                            fill="currentColor" 
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                          />
                        </svg>
                        Envoi en cours...
                      </span>
                    ) : (
                      'Envoyer le lien de réinitialisation'
                    )}
                  </button>
                </form>

                {/* Lien retour connexion */}
                <div className="mt-6 text-center">
                  <button
                    onClick={handleBackToLogin}
                    className="text-sm text-gray-500 hover:text-blue-900 transition-colors inline-flex items-center gap-1"
                  >
                    <ArrowLeft size={14} />
                    Retour à la connexion
                  </button>
                </div>
              </>
            )}
          </>
        ) : (
          /* ==================== FORMULAIRE CONNEXION ==================== */
          <>
            {/* Indicateur de copropriété (si vient d'un portail) */}
            {pendingCoproprieteSlug && (
              <div className="mb-6 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-3">
                <Shield className="text-blue-600" size={20} />
                <p className="text-blue-700 text-sm">
                  Connexion à l'espace copropriété
                </p>
              </div>
            )}

            {/* Message d'erreur */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700 text-sm flex items-center gap-2">
                  <span>⚠️</span>
                  {error}
                </p>
              </div>
            )}

            {/* Session 150 : Message de statut (expiré, suspendu, etc.) */}
            {statusMessage && (
              <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                <p className="text-orange-800 text-sm">{statusMessage}</p>
              </div>
            )}

            {/* Formulaire */}
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email */}
              <div>
                <label 
                  htmlFor="email" 
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Adresse email
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="votre@email.com"
                  required
                  disabled={isSubmitting}
                  autoComplete="email"
                />
              </div>

              {/* Mot de passe */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label 
                    htmlFor="password" 
                    className="block text-sm font-medium text-gray-700"
                  >
                    Mot de passe
                  </label>
                  <button
                    type="button"
                    onClick={handleShowResetForm}
                    className="text-sm text-blue-600 hover:text-blue-800 transition-colors"
                  >
                    Mot de passe oublié ?
                  </button>
                </div>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="••••••••"
                  required
                  disabled={isSubmitting}
                  autoComplete="current-password"
                />
              </div>

              {/* Bouton connexion */}
              <button
                type="submit"
                disabled={isSubmitting}
                className={`
                  w-full py-3 px-4 rounded-lg font-semibold text-white
                  transition-all duration-200
                  ${isSubmitting 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-blue-900 hover:bg-blue-800 hover:shadow-lg'
                  }
                `}
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle 
                        className="opacity-25" 
                        cx="12" cy="12" r="10" 
                        stroke="currentColor" 
                        strokeWidth="4"
                        fill="none"
                      />
                      <path 
                        className="opacity-75" 
                        fill="currentColor" 
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      />
                    </svg>
                    Connexion en cours...
                  </span>
                ) : (
                  'Se connecter'
                )}
              </button>
            </form>

            {/* Lien inscription (si vient d'un portail copro) */}
            {pendingCoproprieteSlug && (
              <div className="mt-6 text-center">
                <p className="text-sm text-gray-500">
                  Pas encore de compte ?{' '}
                  <Link 
                    to="/inscription" 
                    className="text-blue-600 hover:text-blue-800 font-medium"
                  >
                    S'inscrire
                  </Link>
                </p>
              </div>
            )}

            {/* Lien retour */}
            <div className="mt-8 text-center">
              <Link 
                to={backLink}
                className="text-sm text-gray-500 hover:text-blue-900 transition-colors inline-flex items-center gap-1"
              >
                <ArrowLeft size={14} />
                {backLabel}
              </Link>
            </div>
          </>
        )}

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-gray-200 text-center">
          <p className="text-xs text-gray-400">
            Copro Protect • DOM TECH & SERVICES
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
